import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import * as d3 from 'd3';
import { feature as convertTopojsonToGeojson } from 'topojson-client';

// Toggles the main map fade animation when hovering over a country
const HOVER_ANIMATION_ENABLED = true;

// Toggles whether the map should 'clone' the hovered territory to ensure that it's outlines are properly drawn.
// To see what this does, go to the ADM1 story in Storybook and change this to variable and pay close attention
// to territories which have neighbours on all sides. You should see that with this set to off, some outlines are
// not drawn due to layer stacking order in the SVG.
const HOVER_CLONE_EFFECT_ENABLED = true;

// Currently used to inspect map boundary issues
const __WIP_ENABLE_ZOOM = false;

const SVG_MAP_PADDING = 8;

const MOBILE_BREAKPOINT = 360;
const DESKTOP_BREAKPOINT = 1080;

const MARKER_SIZE_MOBILE = 2;
const MARKER_SIZE_DESKTOP = 8;
const markerScale = d3.scaleLinear(
  [MOBILE_BREAKPOINT, DESKTOP_BREAKPOINT],
  [MARKER_SIZE_MOBILE, MARKER_SIZE_DESKTOP]
);

const PATTERN_SIZE_MOBILE = 4;
const PATTERN_SIZE_DESKTOP = 8;
const pattternSizeScale = d3
  .scaleLinear([MOBILE_BREAKPOINT, DESKTOP_BREAKPOINT], [PATTERN_SIZE_MOBILE, PATTERN_SIZE_DESKTOP])
  .clamp(true);

const classNames = {
  // Root SVG
  MAP_HAS_ACTIVE_TERRITORY: 'map__hasActiveTerritory',

  // Layers/groups
  GENERATED_LAYER_CONTENT: 'map__generatedLayerContent',
  NON_MEMBER_LAYER: 'map__layer--non-members',
  MEMBER_LAYER: 'map__layer--members',
  MARKERS_LAYER: 'map__layer--markers',

  // Territory classes
  TERRITORY: 'map__generatedTerritory',
  CLICKABLE_TERRITORY: 'map__clickableTerritory',
  ACTIVE_TERRITORY: 'map__activeTerritory',

  CORE_MEMBER: 'map__coreMember',
  FRIENDS: 'map__friends',
  ASSOCIATES: 'map__associates',

  FUND: 'map__fundOnly',
  FRIENDS_FUND: 'map__friendsFund',

  // Misc
  TERRITORY_HOVER_CLONE: 'map__territoryHoverClone',
};

const PATTERNS = [
  {
    id: 'ecru-pattern',
    background: 'var(--colour--ecru)',
    foreground: 'var(--colour--leather)',
  },
  {
    id: 'indigo-pattern',
    background: 'var(--colour--ecru)',
    foreground: 'var(--colour--indigo)',
  },
];

// TODO: Move to a util file?
function debounce(func) {
  let timer;
  return function (event) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(func, 100, event);
  };
}

function isBelowDesktop() {
  return window.innerWidth < DESKTOP_BREAKPOINT;
}

const ADM0_URL = '/map-data/adm0/world.topojson';
const ADM1_BASE_URL = '/map-data/adm1/';

class BogaMap extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = null;
    this.mapData = null;
    delete this.mapPopover;
  }

  async init() {
    const membersDataAttr = this.getAttribute('data-members');
    this.membersData = JSON.parse(membersDataAttr);
    this.membersDataByFeature = this.membersData.reduce((acc, member) => {
      acc[member.mapFeature] = member;
      return acc;
    }, {});

    const popoverContainer = this.querySelector('.map__popover');
    const dialog = this.querySelector('dialog');

    this.mapContainer = this.querySelector('div.map__layers');

    const nonMembersLayer = this.querySelector('.' + classNames.NON_MEMBER_LAYER);
    const membersLayer = this.querySelector('.' + classNames.MEMBER_LAYER);
    const markersLayer = this.querySelector('.' + classNames.MARKERS_LAYER);

    if (!this.mapContainer || !popoverContainer || !nonMembersLayer) {
      console.warn('[boga-map] Missing required elements');
      return;
    }

    this.layers = {
      nonMembers: nonMembersLayer,
      members: membersLayer,
      markers: markersLayer,
    };

    this.mapDialog = new BogaMapDialog(dialog);
    this.mapPopover = new BogaMapPopover(popoverContainer);

    await this.loadMapData();
    this.drawMap();

    this.observer = new ResizeObserver(
      debounce(() => {
        this.clearMap();
        this.drawMap();
      })
    );

    this.observer.observe(this);

    window.addEventListener('scroll', () => {
      document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
    });
  }

  async loadMapData() {
    const adm0Topology = await d3.json(ADM0_URL);
    const adm0Data = convertTopojsonToGeojson(adm0Topology, 'adm0');

    const adm1Features = [];
    for (const memberData of this.membersData) {
      // ADM1 features have IDs with a dash in them
      if (memberData.mapFeature.includes('-')) {
        const geojson = await d3.json(ADM1_BASE_URL + memberData.mapFeature + '.json');
        adm1Features.push(...geojson.features);
      }
    }

    this.mapData = [].concat(adm0Data.features, adm1Features);
  }

  async drawMap() {
    if (!this.mapData) {
      console.warn('[boga-map]: Cannot draw map until data has been loaded in.');
      return;
    }

    const svgRootSelection = d3.select(this.mapContainer);

    const width = parseInt(svgRootSelection.style('width'));
    const height = parseInt(svgRootSelection.style('height'));

    for (const layerKey in this.layers) {
      const layer = this.layers[layerKey];

      layer.setAttribute('viewBox', `0 0 ${width} ${height}`);
      layer.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    const projection = d3
      .geoNaturalEarth1()
      .fitExtent(
        [
          [SVG_MAP_PADDING, SVG_MAP_PADDING],
          [width - SVG_MAP_PADDING, height - SVG_MAP_PADDING],
        ],
        {
          type: 'FeatureCollection',
          features: this.mapData,
        }
      )
      .center([0, 0])
      .rotate([-12, 0]);
    const pathGenerator = d3.geoPath().projection(projection);

    this.renderNonMemberGeography(d3.select(this.layers.nonMembers), pathGenerator);
    this.renderMemberGeography(d3.select(this.layers.members), pathGenerator);
    this.renderMarkers(d3.select(this.layers.markers), projection);

    if (__WIP_ENABLE_ZOOM) {
      const zoom = d3.zoom().on(
        'zoom',
        function (event) {
          for (const layerKey in this.layers) {
            const layer = this.layers[layerKey];

            const layerContent = layer.getElementsByClassName(classNames.GENERATED_LAYER_CONTENT);

            layerContent[0].setAttribute('transform', event.transform);
          }
        }.bind(this)
      );

      svgRootSelection.call(zoom);
    }
  }

  renderNonMemberGeography(mapLayerSelection, pathGenerator) {
    const nonMemberFeatures = this.mapData.filter((territory) => {
      const { i: iso } = territory.properties;

      const isMember = this.membersDataByFeature[iso];

      return !isMember;
    });

    mapLayerSelection
      .append('g')
      .attr('class', classNames.GENERATED_LAYER_CONTENT)
      .selectAll('path')
      .data(nonMemberFeatures)
      .join('path')
      .attr('d', pathGenerator)
      .attr('data-iso', (territory) => territory.properties.i)
      .attr('class', classNames.TERRITORY);
  }

  renderMemberGeography(mapLayerSelection, pathGenerator) {
    const memberFeatures = this.mapData.filter((territory) => {
      const { i: iso } = territory.properties;

      const isMember = this.membersDataByFeature[iso];

      return isMember;
    });

    const node = mapLayerSelection.node();
    node.appendChild(this.createPatterns());

    mapLayerSelection
      .append('g')
      .attr('class', classNames.GENERATED_LAYER_CONTENT)
      .selectAll('path')
      .data(memberFeatures)
      .join('path')
      .attr('d', pathGenerator)
      .attr('id', (territory) => territory.properties.i)
      .attr('data-iso', (territory) => territory.properties.i)
      .attr('class', (territory) => {
        const { i: iso } = territory.properties;
        const status = this.membersDataByFeature[iso];

        if (!status) return classNames.TERRITORY;

        return [
          classNames.TERRITORY,
          classNames.CLICKABLE_TERRITORY,
          this.getTerritoryClass(iso),
        ].join(' ');
      })
      .on('mouseover', (event, territory) => {
        const { i: iso } = territory.properties;

        this.handleTerritoryHover(event, iso);
      })
      .on('mousemove', (event) => this.mapPopover.updatePosition(event))
      .on('mouseout', () => {
        this.setActiveTerritory(null);
        this.mapPopover.hide();
      })
      .on('click', (_, territory) => {
        const { i: iso } = territory.properties;

        this.handleTerritoryClick(iso);
      });
  }

  renderMarkers(mapLayerSelection, projection) {
    const smallCountries = this.mapData.filter(
      (e) =>
        e.properties.t === 0 && e.properties.s === 1 && !!this.membersDataByFeature[e.properties.i]
    );

    const size = markerScale(window.innerWidth);
    const markerClassName = [classNames.TERRITORY, classNames.CLICKABLE_TERRITORY].join(' ');

    mapLayerSelection
      .append('g')
      .attr('class', classNames.GENERATED_LAYER_CONTENT)
      .selectAll('rect')
      .data(smallCountries)
      .join('rect')
      .attr('class', (territory) => {
        const { i: iso } = territory.properties;

        const className = this.getTerritoryClass(iso);

        return [markerClassName, className].join(' ');
      })
      .attr('x', (t) => projection([t.properties.c[0], t.properties.c[1]])[0] - size / 2)
      .attr('y', (t) => projection([t.properties.c[0], t.properties.c[1]])[1] - size / 2)
      .attr('width', size)
      .attr('height', size)
      .attr('data-iso', (territory) => territory.properties.i)
      .on('mouseover', (event, territory) => {
        const { i: iso } = territory.properties;

        this.handleTerritoryHover(event, iso);
      })
      .on('mousemove', (event) => this.mapPopover.updatePosition(event))
      .on('mouseout', () => {
        this.setActiveTerritory(null);
        this.mapPopover.hide();
      })
      .on('click', (_, territory) => {
        const { i: iso } = territory.properties;

        this.handleTerritoryClick(iso);
      });
  }

  clearMap() {
    // const generatedLayers = this.mapContainer.querySelectorAll('.' + classNames.GENERATED_MAP);
    // Array.prototype.forEach.call(generatedLayers, (layer) => layer.remove());

    for (const layerKey in this.layers) {
      const layerEl = this.layers[layerKey];
      layerEl.innerHTML = '';
    }
  }

  setActiveTerritory(iso) {
    if (!HOVER_ANIMATION_ENABLED) return;

    if (!iso) {
      this.mapContainer.classList.remove(classNames.MAP_HAS_ACTIVE_TERRITORY);

      Array.prototype.forEach.call(
        this.mapContainer.getElementsByClassName(classNames.ACTIVE_TERRITORY),
        (e) => e.classList.remove(classNames.ACTIVE_TERRITORY)
      );

      if (!HOVER_CLONE_EFFECT_ENABLED) return;
      Array.prototype.forEach.call(this.mapContainer.querySelectorAll('[data-clone]'), (e) =>
        e.remove()
      );
    } else {
      this.mapContainer.classList.add(classNames.MAP_HAS_ACTIVE_TERRITORY);

      // querySelectorAll is used as there can be both a geography element + marker representing a
      // territory
      const territoryElements = this.mapContainer.querySelectorAll(`[data-iso="${iso}"]`);
      if (territoryElements.length === 0) return;

      Array.prototype.forEach.call(territoryElements, (e) => {
        e.classList.add(classNames.ACTIVE_TERRITORY);

        if (!HOVER_CLONE_EFFECT_ENABLED) return;

        const clonedNode = e.cloneNode();
        clonedNode.setAttribute('data-clone', true);
        clonedNode.classList.add(classNames.TERRITORY_HOVER_CLONE);

        e.parentNode.appendChild(clonedNode);
      });
    }
  }

  handleTerritoryClick(iso) {
    if (isBelowDesktop()) return;

    const data = this.membersDataByFeature[iso];
    if (!data) return;

    // Fund country but not BOGA member
    // if (!data.type && data.fund) {
    //   window.location = data.url;
    //   return;
    // }

    this.mapDialog.open({
      iso,
      url: data.url,
    });
  }

  handleTerritoryHover(event, iso) {
    if (isBelowDesktop()) return;

    const data = this.membersDataByFeature[iso];
    if (!data) return this.setActiveTerritory();

    this.setActiveTerritory(iso);
    this.mapPopover.show(event, {
      name: this.membersDataByFeature[iso]?.name,
      fund: this.membersDataByFeature[iso]?.fund,
      type: this.membersDataByFeature[iso]?.type,
    });
  }

  getTerritoryClass(iso) {
    const status = this.membersDataByFeature[iso];

    if (!status) return '';

    if (status.type === 'core-members') return classNames.CORE_MEMBER;
    if (status.type === 'associates') return classNames.ASSOCIATES;

    if (status.type === 'friends' && status.fund === true) return classNames.FRIENDS_FUND;
    if (status.type === 'friends') return classNames.FRIENDS;

    if (status.fund === true) return classNames.FUND;

    return '';
  }

  createPatterns() {
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const screenWidth = window.innerWidth;
    const patternSize = pattternSizeScale(screenWidth);

    for (const pattern of PATTERNS) {
      const patternElement = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
      patternElement.id = pattern.id;

      patternElement.setAttribute('patternUnits', 'userSpaceOnUse');
      patternElement.setAttribute('preserveAspectRatio', 'none meet');
      patternElement.setAttribute('viewbox', `0 0 ${patternSize} ${patternSize}`);
      patternElement.setAttribute('width', patternSize);
      patternElement.setAttribute('height', patternSize);

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', 0);
      rect.setAttribute('y', 0);
      rect.setAttribute('width', patternSize);
      rect.setAttribute('height', patternSize);
      rect.setAttribute('fill', pattern.background);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', patternSize / 2);
      circle.setAttribute('cy', patternSize / 2);
      circle.setAttribute('r', patternSize / 2);
      circle.setAttribute('fill', pattern.foreground);

      patternElement.appendChild(rect);
      patternElement.appendChild(circle);

      defs.appendChild(patternElement);
    }

    return defs;
  }
}

class BogaMapPopover {
  constructor(container) {
    this.popoverElement = container;

    this.titleElement = container.querySelector('.map__popoverTitle');
    this.tagsListElement = this.createVirtualElement();
  }

  show(event, { name, fund, type }) {
    this.titleElement.textContent = name;

    const tags = this.popoverElement.querySelector('ul.map__tags');
    if (tags) {
      tags.remove();
    }

    this.updateTags({ type, fund });

    this.popoverElement.style.display = 'flex';
    this.updatePosition(event);
  }

  updateTags({ fund, type }) {
    const tagsListElement = document.createElement('ul');
    tagsListElement.className = 'map__popoverRow map__tags';

    if (!type && !fund) return;

    if (type) {
      const tag = createTag(type);
      if (!tag) return;
      tagsListElement.appendChild(tag);
    }

    if (fund) {
      const tag = createTag('fund');
      tagsListElement.appendChild(tag);
    }

    this.popoverElement.appendChild(tagsListElement);
  }

  updatePosition(event) {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;

    computePosition(this.virtualElement, this.popoverElement, {
      placement: 'top-start',
      middleware: [offset(10), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      this.popoverElement.style.left = x + 'px';
      this.popoverElement.style.top = y + 'px';
    });
  }

  hide() {
    this.popoverElement.style.display = 'none';

    const tags = this.popoverElement.querySelector('ul.map__tags');
    if (tags) {
      tags.remove();
    }
  }

  createVirtualElement() {
    this.virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: this.mouseX,
        y: this.mouseY,
        top: this.mouseY,
        left: this.mouseX,
        right: this.mouseX,
        bottom: this.mouseY,
      }),
    };
  }
}

class BogaMapDialog {
  constructor(dialogEl) {
    this.dialogEl = dialogEl;
    this.contentWrapper = this.dialogEl.querySelector('.map__dialogContent');
  }

  open({ iso, url }) {
    this.dialogEl.showModal();

    const closeButton = this.dialogEl.querySelector('.map__dialogCloseButton');
    closeButton.onclick = () => {
      this.close();
    };

    const scrollY = document.documentElement.style.getPropertyValue('--scroll-y');
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}`;

    this.loadRemoteContent(url);
  }

  close() {
    this.dialogEl.close();
    this.contentWrapper.innerHTML = '';
    const body = document.body;
    const scrollY = body.style.top;
    body.style.position = '';
    body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }

  async loadRemoteContent(url) {
    const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const html = await resp.text();
    this.contentWrapper.innerHTML = html;
  }
}

const TAG_DATA = {
  friends: {
    className: 'map__tagItem--friends',
    label: 'Friend',
  },
  'core-members': {
    className: 'map__tagItem--core-members',
    label: 'Core member',
  },
  associates: {
    className: 'map__tagItem--associates',
    label: 'Associate member',
  },
  fund: {
    className: 'map__tagItem--fund',
    label: 'Fund programme',
  },
};

function createTag(type) {
  const data = TAG_DATA[type];
  if (!data) return;

  const li = document.createElement('li');
  li.className = data.className;

  const tag = document.createElement('div');
  tag.className = 'tag';
  tag.textContent = data.label;

  li.appendChild(tag);

  return li;
}

window.customElements.define('boga-map', BogaMap);
